import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { sanitizeProps } from '@fs/zion-frontend-friends'
import { Expander, ExpanderContent, InlineButton } from '@fs/zion-ui'

const collapsedCss = css`
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
`

const buttonContainerCss = css`
  margin-top: 4px;
`
const hide = css`
  visibility: hidden;
`

function getPx(value) {
  const numStr = value.match(/\d+/)
  return numStr ? parseInt(numStr, 10) : 0 // this 0 is a failsafe for JSDOM testing. Manual testing in browsers proved that numStr is always defined.
}

function getTextContainer(el) {
  let textChild = el
  while (textChild.firstChild && textChild.firstChild.nodeType !== Node.TEXT_NODE) {
    textChild = textChild.firstElementChild
  }
  return textChild
}

export default function LifeSketchMoreLess({
  children,
  collapsedLineCount = 2,
  buttonProps,
  showMore,
  onMoreLessClick,
  externalChildHeight,
  setLifeSketchExpandedHeight,
  isSurvivingPerson,
  originalChildHeight,
  ...props
}) {
  // Whether it is expanded or collapsed
  const [open, setOpen] = useState(false)

  // The height of the text when collapsed
  const [collapsedHeight, setCollapsedHeight] = useState()

  // Whether or not the lines are clamped (ellipsed)
  const [clampLines, setClampLines] = useState(true)

  // The largest child's height of the text when it is expanded
  const [expandedChildHeight, setExpandedChildHeight] = useState()

  // The height of the text for the actual child height
  const [showMoreLessButton, setShowMoreLessButton] = useState()

  // Keep track of the button so that we can blur onClick
  const buttonRef = useRef(null)

  // Ref to the text element
  const textRef = useRef(null)

  const [t] = useTranslation()

  // Once it has collapsed, clamp the lines
  function onTransitionEnd() {
    setClampLines(true)
  }

  const handleClick = useCallback(() => {
    // When expanding, disable clamping lines
    if (clampLines) {
      setClampLines(false)
    }
    if (onMoreLessClick) {
      onMoreLessClick()
    } else {
      setOpen((o) => !o)
    }
    buttonRef?.current?.blur()
  }, [clampLines, onMoreLessClick])

  useEffect(() => {
    setOpen(showMore)
  }, [showMore])

  // Calculate the height of the text when it is expanded
  useEffect(() => {
    const textChild = getTextContainer(textRef.current.firstElementChild)
    const compStyle = getComputedStyle(textChild)
    const bottomPadding = compStyle.getPropertyValue('padding-bottom')
    setExpandedChildHeight(textChild.clientHeight - getPx(bottomPadding)) // Remove the bottom padding height so the button will more tightly hug the text
    setLifeSketchExpandedHeight(textChild.clientHeight - getPx(bottomPadding), isSurvivingPerson)
  }, [setLifeSketchExpandedHeight, isSurvivingPerson])

  // Calculate the collapsed height
  useEffect(() => {
    const textChild = getTextContainer(textRef.current.firstElementChild)
    const compStyle = getComputedStyle(textChild)
    const compLineHeight = compStyle.getPropertyValue('line-height')
    let lineHeight = ''

    if (compLineHeight === 'normal') {
      const fontSize = getPx(compStyle.getPropertyValue('font-size'))
      lineHeight = fontSize * 1.2 // this is the value of line-height when it is 'normal', rather than a number
    } else {
      lineHeight = getPx(compLineHeight)
    }

    const height = lineHeight * collapsedLineCount
    const paddingTop = getPx(compStyle.getPropertyValue('padding-top'))
    const paddingBottom = getPx(compStyle.getPropertyValue('padding-bottom'))
    const total = height + paddingTop // No padding bottom because it exposes extra lines when the line-height is close together
    // In firefox the (total + padding) is < the expandedChildHeight, but only by a fraction of a px.
    setCollapsedHeight(expandedChildHeight - (total + paddingBottom) > 1 ? total : null)
    setShowMoreLessButton(originalChildHeight - (total + paddingBottom) > 1 ? total : null)
  }, [collapsedLineCount, expandedChildHeight, originalChildHeight])

  useEffect(() => {
    setExpandedChildHeight(externalChildHeight)
  }, [externalChildHeight])

  return (
    <div
      {...sanitizeProps(props)}
      style={{
        height: collapsedHeight !== undefined ? 'auto' : 0,
        overflow: collapsedHeight !== undefined ? 'visible' : 'hidden',
      }}
    >
      <Expander open={open} setOpen={setOpen}>
        <ExpanderContent
          collapsedHeight={collapsedHeight}
          // Only give it the transition end handler when it is going to be collapsed (i.e. they just clicked the "Less" button)
          onTransitionEnd={!open ? onTransitionEnd : null}
        >
          <div
            ref={textRef}
            css={clampLines && collapsedHeight !== undefined ? collapsedCss : undefined}
            style={{ whiteSpace: 'pre-line', '--line-clamp': collapsedLineCount }}
          >
            <div>{children}</div>
          </div>
        </ExpanderContent>
      </Expander>
      {/* Only show the button when we have a collapsedHeight */}
      {collapsedHeight && (
        <div css={!showMoreLessButton && hide}>
          <div css={buttonContainerCss}>
            <InlineButton
              {...sanitizeProps(buttonProps)}
              onClick={handleClick}
              keyline="left"
              aria-hidden
              ref={buttonRef}
            >
              {open ? t('less', 'Less') : t('more', 'More')}
            </InlineButton>
          </div>
        </div>
      )}
    </div>
  )
}

LifeSketchMoreLess.propTypes = {
  /** The text component to truncate. */
  children: PropTypes.node.isRequired,

  /** The number of lines to show when the text is collapsed. */
  collapsedLineCount: PropTypes.number,

  /** Add custom props to the more/less button. */
  buttonProps: PropTypes.object, // eslint-disable-line react/forbid-prop-types
}
